export namespace dataSource {
    export function isDataSource(data: any) {
        return data instanceof ObjectDataSource || data instanceof ArrayDataSource || _.isFunction(data['getRowCount']);
    }

    export function CreateFromArrayWithNames(rows: any[][], names: string[]): IDataSource {
        return new ArrayDataSource(rows, names);
    }

    export function CreateFromObjectArray(rows: any[], names?: string[]): IDataSource {
        return new ObjectDataSource(rows, typeof names === 'undefined' ? null : names);
    }

    export interface IDataSource {
        getRowCount(): number;

        getSource(): any[];

        getValue(rowIndex: number, column: number): any;

        setValue(rowIndex: number, column: number, value: any): void;

        getValue(rowIndex: number, column: string): any;

        setValue(rowIndex: number, column: string, value: any): void;

        append(data: any[]): void;

        move(oldIndex: number, newIndex: number);

        remove(dataIndex: number, count?: number): any;
    }

    class BaseDataSource {
        public _rows: any[];

        constructor(rows: any[]) {
            this._rows = rows;
        }

        public getRowCount(): number {
            return this._rows.length;
        }

        public getSource(): any[] {
            return this._rows;
        }

        public append(data: any[]): void {
            this._rows.push(...data);
        }

        public remove(index: number, count = 1) {
            this._rows.splice(index, count);
        }

        public move(oldIndex: number, newIndex: number) {
            this._rows.move(oldIndex, newIndex);
        }
    }

    interface leafObjectAccessor {
        Object: any;
        propertyName: string;
    }

    interface IAccessor {
        get: (obj: any) => any;
        set: (obj: any, value: any) => any;
    }

    class ObjectDataSource extends BaseDataSource implements IDataSource {
        public _names: string[];

        constructor(rows: any[], names: string[]) {
            super(rows);
            this._names = names;
        }

        public getValue(rowIndex: number, column: number): any;
        public getValue(rowIndex: number, column: string): any;
        public getValue(rowIndex: number, column: any): any {
            return this.getLeafObj(rowIndex, column);
        }

        private getLeafObj(rowIndex: number, column: any): any {
            const row = this._rows[rowIndex];
            if (row.hasOwnProperty(column)) {
                return row[column];
            }
            const columnName = this.getColumnName(column);
            const split = columnName.split('.');
            let leafObj = row;
            let i = 0;
            for (; i < split.length; i++) {
                if (!leafObj) {
                    break;
                }

                leafObj = leafObj[split[i]];
            }
            return leafObj;
        }

        private setLeafObj(rowIndex: number, column: any, value): any {
            const row = this._rows[rowIndex];
            const columnName = this.getColumnName(column);
            const split = columnName.split('.');
            let leafObj = row;
            let i = 0;
            for (; i < split.length - 1; i++) {
                leafObj = leafObj[split[i]];
            }
            leafObj[split[i]] = value;
        }

        public setValue(rowIndex: number, column: string, value: any): void;
        public setValue(rowIndex: number, column: number, value: any): void;
        public setValue(rowIndex: number, column: any, value: any): void {
            const accessor = this.setLeafObj(rowIndex, column, value);
        }

        private getColumnName(column: any): string {
            if (typeof column === 'string') {
                return column;
            }
            if (typeof column === 'number') {
                const name = this.getNameByIndex(column);
                if (typeof name !== 'undefined') {
                    return name;
                }
            }
            throw 'Not support column identifier ' + column;
        }

        private getNameByIndex(index: number): string {
            if (this._names == null) {
                throw 'names not support for ObjectDataSource';
            }
            return this._names[index];
        }
    }

    class ArrayDataSource extends BaseDataSource implements IDataSource {
        private _nameIndexes: IMap<number> = null;

        constructor(rows: any[], names: string[] = null) {
            super(rows);
            if (names != null) {
                this._nameIndexes = _.zipObject(names, _.range(names.length)) as IMap<number>;
            }
        }

        public getValue(rowIndex: number, column: number): any;
        public getValue(rowIndex: number, column: string): any;
        public getValue(rowIndex: number, column: any): any {
            const row = this._rows[rowIndex];
            return row[this.getColumnIndex(column)];
        }

        public setValue(rowIndex: number, column: string, value: any): void;
        public setValue(rowIndex: number, column: number, value: any): void;
        public setValue(rowIndex: number, column: any, value: any): void {
            const row = this._rows[rowIndex];
            row[this.getColumnIndex(column)] = value;
        }

        private getColumnIndex(column: any): number {
            if (typeof column === 'number') {
                return column;
            }
            if (typeof column === 'string') {
                const indexByName = this.getIndexByName(column);
                if (typeof indexByName !== 'undefined') {
                    return indexByName;
                }
            }
            throw 'Not support column identifier ' + column;
        }

        private getIndexByName(name: string): number {
            if (this._nameIndexes == null) {
                throw 'names not support for ArrayDataSource';
            }
            return this._nameIndexes[name];
        }
    }

    export class EmptyDataSource implements dataSource.IDataSource {
        public getRowCount(): number {
            return 0;
        }

        public getSource(): any[] {
            return null;
        }

        public getValue(rowIndex: number, column: number): any;
        public getValue(rowIndex: number, column: string): any;
        public getValue(rowIndex: number, column: any): any {
            return null;
        }

        public setValue(rowIndex: number, column: number, value: any): void;
        public setValue(rowIndex: number, column: string, value: any): void;
        public setValue(rowIndex: number, column: any, value: any): void {}

        public append(data: any[]): void {}

        public remove(i: number) {}

        public move(oldIndex: number, newIndex: number) {}
    }
}
